import { useSignUp, useAuth, useSession } from "@clerk/clerk-react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import s from "./styles.module.scss";
import PageHeader from "../../components/page-header";
import InAppBrowserDetector from "../../components/in-app-browser-detector";
import Input from "../../components/input";
import Button from "../../components/button";
import SchoolDropdown from "../../components/school-dropdown";
import apiClient from "../../services/api-client";
import React from "react";

const ClerkSignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded, signUp, setActive } = useSignUp();
  const { session } = useSession();

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [school, setSchool] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");

  // UI state
  const [pendingVerification, setPendingVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState("");
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  // Restore school from sessionStorage after OAuth redirect
  useEffect(() => {
    const savedSchool = sessionStorage.getItem("signup_school");
    if (savedSchool) {
      try {
        setSchool(JSON.parse(savedSchool));
      } catch (e) {
        console.error("Failed to parse saved school:", e);
      }
    }
  }, []);

  // Redirect if already signed in (handles OAuth return)
  useEffect(() => {
    const syncSchoolAndRedirect = async () => {
      if (authLoaded && isSignedIn && session) {
        // Check if there's a school in sessionStorage (from OAuth flow)
        const savedSchool = sessionStorage.getItem("signup_school");
        if (savedSchool) {
          try {
            const schoolData = JSON.parse(savedSchool);
            if (schoolData?.school_id || schoolData?.custom_school) {
              const getToken = async () => session.getToken();
              await apiClient.post(
                "/user/school",
                {
                  school_id: schoolData.school_id || null,
                  custom_school: schoolData.custom_school || null
                },
                getToken
              );
            }
          } catch (err) {
            console.error("Error syncing school after OAuth:", err);
          } finally {
            sessionStorage.removeItem("signup_school");
          }

          // Meta Pixel flag is already set in handleOAuthSignup before redirect
        }
        navigate("/upload", { replace: true });
      }
    };
    syncSchoolAndRedirect();
  }, [authLoaded, isSignedIn, session, navigate]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!school?.school_name && !school?.custom_school) {
      newErrors.school = "Please select your school";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle initial signup submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    if (!validateForm()) return;
    if (!isLoaded) return;

    setLoading(true);

    try {
      // Create the signup with school in metadata
      await signUp.create({
        firstName,
        lastName,
        emailAddress: email,
        password,
        unsafeMetadata: {
          school_id: school.school_id,
          school_name: school.school_name,
          custom_school: school.custom_school || null,
        },
      });

      // Send verification email
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setPendingVerification(true);
    } catch (err) {
      console.error("Signup error:", err);

      // Handle Clerk errors
      if (err.errors && err.errors.length > 0) {
        const clerkError = err.errors[0];
        if (clerkError.code === "form_identifier_exists") {
          setGeneralError("An account with this email already exists. Please sign in instead.");
        } else if (clerkError.code === "form_password_pwned") {
          setErrors({ password: "This password has been compromised in a data breach. Please choose a different password." });
        } else if (clerkError.code === "form_password_length_too_short") {
          setErrors({ password: "Password must be at least 8 characters" });
        } else {
          // Try to get the most descriptive error message
          const errorMessage = clerkError.longMessage || clerkError.message || clerkError.code || "Failed to create account. Please try again.";
          setGeneralError(errorMessage);
        }
      } else if (err.message) {
        setGeneralError(err.message);
      } else {
        setGeneralError("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code submission
  const handleVerify = async (e) => {
    e.preventDefault();
    setGeneralError("");

    if (!verificationCode.trim()) {
      setErrors({ verification: "Please enter the verification code" });
      return;
    }

    if (!isLoaded) return;

    setLoading(true);

    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (completeSignUp.status === "complete") {
        // Flag to fire Meta Pixel after redirect to upload page
        sessionStorage.setItem("pending_signup_pixel", "true");

        // Set the session active
        await setActive({ session: completeSignUp.createdSessionId });

        // Sync school to backend
        try {
          if (school?.school_id || school?.custom_school) {
            const getToken = async () => {
              return await completeSignUp.createdSession?.getToken();
            };
            await apiClient.post(
              "/user/school",
              {
                school_id: school.school_id || null,
                custom_school: school.custom_school || null
              },
              getToken
            );
          }
        } catch (syncError) {
          console.error("Error syncing school to backend:", syncError);
          // Don't block signup - school is in Clerk metadata as fallback
        }

        // Redirect to upload
        navigate("/upload", { replace: true });
      } else {
        console.error("Signup incomplete:", completeSignUp);
        console.error("Status:", completeSignUp.status);
        console.error("Missing fields:", completeSignUp.missingFields);
        console.error("Unverified fields:", completeSignUp.unverifiedFields);
        setGeneralError(`Verification failed: ${completeSignUp.status}. Check console for details.`);
      }
    } catch (err) {
      console.error("Verification error:", err);

      if (err.errors) {
        const clerkError = err.errors[0];
        if (clerkError.code === "form_code_incorrect") {
          setErrors({ verification: "Incorrect verification code. Please try again." });
        } else {
          setGeneralError(clerkError.message || "Verification failed. Please try again.");
        }
      } else {
        setGeneralError("Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (!isLoaded) return;

    setLoading(true);
    setGeneralError("");

    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setGeneralError(""); // Clear any previous error
      alert("Verification code sent! Please check your email.");
    } catch (err) {
      console.error("Resend error:", err);
      setGeneralError("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth signup (Google/Microsoft)
  const handleOAuthSignup = async (provider) => {
    // Validate school selection before OAuth
    if (!school?.school_name && !school?.custom_school) {
      setErrors({ school: "Please select your school before continuing" });
      return;
    }

    setOauthLoading(provider);
    setGeneralError("");
    setErrors({});

    try {
      // Save school to sessionStorage to restore after OAuth redirect
      sessionStorage.setItem("signup_school", JSON.stringify(school));
      // Flag to fire Meta Pixel after OAuth redirect completes
      sessionStorage.setItem("pending_signup_pixel", "true");

      // Start OAuth flow - redirect URLs configured in provider consoles
      await signUp.authenticateWithRedirect({
        strategy: provider === "google" ? "oauth_google" : "oauth_microsoft",
        redirectUrl: window.location.origin + "/sign-up",
        redirectUrlComplete: "/sign-up",
        unsafeMetadata: {
          school_id: school.school_id,
          school_name: school.school_name,
          custom_school: school.custom_school || null,
        },
      });
    } catch (err) {
      console.error("OAuth error:", err);
      setOauthLoading("");

      if (err.errors) {
        const clerkError = err.errors[0];
        setGeneralError(clerkError.message || "Failed to start sign up. Please try again.");
      } else {
        setGeneralError("Failed to start sign up. Please try again.");
      }
    }
  };

  if (!isLoaded) {
    return null;
  }

  // Verification screen
  if (pendingVerification) {
    return (
      <InAppBrowserDetector>
        <div className={s.authContainer}>
          <PageHeader title="Verify your email" />

          <div className={s.cardBox}>
            <form onSubmit={handleVerify} className={s.customForm}>
              <p className={s.verificationText}>
                We've sent a verification code to <strong>{email}</strong>
              </p>

              {generalError && (
                <div className={s.errorBanner}>{generalError}</div>
              )}

              <Input
                type="text"
                name="verificationCode"
                value={verificationCode}
                onChange={setVerificationCode}
                placeholder="Enter verification code"
                error={errors.verification}
                autoComplete="one-time-code"
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify Email"}
              </Button>

              <button
                type="button"
                className={s.resendButton}
                onClick={handleResendCode}
                disabled={loading}
              >
                Didn't receive the code? Resend
              </button>
            </form>
          </div>
        </div>
      </InAppBrowserDetector>
    );
  }

  // Signup form
  return (
    <InAppBrowserDetector>
      <div className={s.authContainer}>
        <PageHeader title="Create your account" />

        <div className={s.cardBox}>
          <form onSubmit={handleSubmit} className={s.customForm}>
            {generalError && (
              <div className={s.errorBanner}>{generalError}</div>
            )}

            {/* School selection first - required for both email and OAuth signup */}
            <div className={s.formField}>
              <label className={s.formLabel}>School</label>
              <SchoolDropdown
                value={school}
                onChange={setSchool}
                placeholder="Search for your school..."
                hasError={!!errors.school}
                required
              />
              {errors.school && (
                <div className={s.fieldError}>{errors.school}</div>
              )}
            </div>

            {/* OAuth buttons */}
            <div className={s.oauthButtons}>
              <button
                type="button"
                className={s.oauthButton}
                onClick={() => handleOAuthSignup("google")}
                disabled={oauthLoading || loading}
              >
                <svg className={s.oauthIcon} viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {oauthLoading === "google" ? "Connecting..." : "Continue with Google"}
              </button>

              <button
                type="button"
                className={s.oauthButton}
                onClick={() => handleOAuthSignup("microsoft")}
                disabled={oauthLoading || loading}
              >
                <svg className={s.oauthIcon} viewBox="0 0 24 24">
                  <path fill="#F25022" d="M1 1h10v10H1z" />
                  <path fill="#00A4EF" d="M1 13h10v10H1z" />
                  <path fill="#7FBA00" d="M13 1h10v10H13z" />
                  <path fill="#FFB900" d="M13 13h10v10H13z" />
                </svg>
                {oauthLoading === "microsoft" ? "Connecting..." : "Continue with Microsoft"}
              </button>
            </div>

            <div className={s.divider}>
              <span className={s.dividerText}>or</span>
            </div>

            <div className={s.nameFields}>
              <div className={s.formField}>
                <label className={s.formLabel}>First name</label>
                <Input
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={setFirstName}
                  placeholder="First name"
                  error={errors.firstName}
                  autoComplete="given-name"
                />
              </div>

              <div className={s.formField}>
                <label className={s.formLabel}>Last name</label>
                <Input
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={setLastName}
                  placeholder="Last name"
                  error={errors.lastName}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className={s.formField}>
              <label className={s.formLabel}>Email address</label>
              <Input
                type="email"
                name="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                error={errors.email}
                autoComplete="email"
              />
            </div>

            <div className={s.formField}>
              <label className={s.formLabel}>Password</label>
              <Input
                type="password"
                name="password"
                value={password}
                onChange={setPassword}
                placeholder="At least 8 characters"
                error={errors.password}
                autoComplete="new-password"
              />
            </div>

            <div id="clerk-captcha" />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading || oauthLoading}
            >
              {loading ? "Creating account..." : "Create account"}
            </Button>

            <div className={s.footerText}>
              Already have an account?{" "}
              <Link to="/sign-in" className={s.footerLink}>
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </InAppBrowserDetector>
  );
};

export default ClerkSignUp;
